import axios from 'axios';
import { HOST_API } from 'src/config-global';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: HOST_API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Add tokens to every request
axiosInstance.interceptors.request.use(
  (config) => {
    // 1. Add APP-LEVEL TOKEN (for all requests)
    const appToken = process.env.REACT_APP_TOKEN;
    if (appToken) {
      config.headers['X-App-Token'] = appToken;
    }

    // 2. Add USER TOKEN (if user is logged in)
    const userToken = localStorage.getItem('accessToken');
    if (userToken) {
      config.headers.Authorization = `Bearer ${userToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - Handle errors and token refresh
axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized - Token expired
    // BUT skip auto-redirect if this is a login attempt (user is trying to login)
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      originalRequest._retry = true;

      try {
        // Clear tokens and redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('user');
        window.location.href = '/auth/jwt/login';
      } catch (err) {
        return Promise.reject(err);
      }
    }

    // Handle 403 Forbidden - Insufficient rights
    if (error.response?.status === 403) {
      const errorMessage =
        error.response.data?.message || 'Access denied. You do not have sufficient rights.';
      const contactMessage =
        error.response.data?.contactMessage || 'Please contact your administrator.';

      // Show error notification if available (will be caught by components with snackbar)
      console.error(`403 Error: ${errorMessage} ${contactMessage}`);

      // Only redirect if the caller has NOT opted out (background/notification fetches should not redirect)
      if (!originalRequest._skipRedirectOn403) {
        setTimeout(() => {
          window.location.href = '/403';
        }, 1500);
      }
    }

    return Promise.reject((error.response && error.response.data) || 'Something went wrong');
  }
);

export default axiosInstance;

// ----------------------------------------------------------------------

// GET fetcher (for non-list endpoints and backward compatibility)
export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];
  const res = await axiosInstance.get(url, { ...config });
  return res.data;
};

// POST fetcher (for list endpoints with field selection)
// POST fetcher (for list endpoints with field selection)
export const postFetcher = async (args) => {
  const [url, body, config] = Array.isArray(args) ? args : [args, {}, {}];
  const res = await axiosInstance.post(url, body, { ...config });
  return res.data;
};

// ----------------------------------------------------------------------

export const endpoints = {
  kanban: '/api/kanban',
  calendar: '/api/calendar',
  auth: {
    me: '/admin/auth/me',
    // login: '/api/auth/login',
    login: '/admin/auth/login',
    register: '/api/auth/register',
    changePassword: '/admin/auth/change-password',
    captcha: '/admin/auth/captcha',
    // logout: '/api/auth/logout',
  },
  mail: {
    list: '/api/mail/list',
    details: '/api/mail/details',
    labels: '/api/mail/labels',
  },
  post: {
    list: '/api/post/list',
    details: '/api/post/details',
    latest: '/api/post/latest',
    search: '/api/post/search',
  },
  product: {
    list: '/api/product/list',
    details: '/api/product/details',
    search: '/api/product/search',
  },
  // user: {
  //   list: '/api/user/list',
  //   details: '/api/user/details',
  // },

  user: {
    list: '/api/admin/users', // Will add query params dynamically
    details: (id) => `/api/admin/users/${id}`,
    create: '/api/admin/users',
    update: (id) => `/api/admin/users/${id}`,
    delete: (id) => `/api/admin/users/${id}`,
    profileStats: (id) => `/api/admin/users/${id}/profile-stats`,
    profileProjectsTeam: (id) => `/api/admin/users/${id}/profile-projects-team`,
    profileTimeline: (id) => `/api/admin/users/${id}/profile-timeline`,
  },
  adminUser: {
    list: '/api/admin/admin-users/list',
    create: '/api/admin/admin-users',
    details: (id) => `/api/admin/admin-users/${id}`,
    update: (id) => `/api/admin/admin-users/${id}`,
    delete: (id) => `/api/admin/admin-users/${id}`,
    changePassword: (id) => `/api/admin/admin-users/${id}/change-password`,
  },
  appUser: {
    list: '/api/admin/app-users/list', // Updated to POST
    // create: null, // No create needed
    details: (id) => `/api/admin/app-users/${id}`,
    update: (id) => `/api/admin/app-users/${id}`,
    delete: (id) => `/api/admin/app-users/${id}`,
  },
  organization: {
    list: '/api/admin/organizations/list',
    // list: '/api/admin/organizations',
    create: '/api/admin/organizations',
    details: (id) => `/api/admin/organizations/${id}`,
    update: (id) => `/api/admin/organizations/${id}`,
    delete: (id) => `/api/admin/organizations/${id}`,
    backup: (id) => `/api/admin/organizations/${id}/backup`,
    getExtraDetailsForm: (id) => `/api/admin/organizations/${id}/extra-details-form`,
    saveExtraDetailsForm: (id) => `/api/admin/organizations/${id}/extra-details-form`,
    currentExtraDetailsForm: '/api/admin/organizations/current/extra-details-form',
  },
  communityCategory: {
    list: '/api/admin/categories',
    create: '/api/admin/categories',
    details: (id) => `/api/admin/categories/${id}`,
    update: (id) => `/api/admin/categories/${id}`,
    delete: (id) => `/api/admin/categories/${id}`,
  },

  country: {
    list: '/api/admin/countries',
    create: '/api/admin/countries',
    details: (id) => `/api/admin/countries/${id}`,
    update: (id) => `/api/admin/countries/${id}`,
    delete: (id) => `/api/admin/countries/${id}`,
  },
  reportReason: {
    list: '/api/admin/report-reasons',
    create: '/api/admin/report-reasons',
    details: (id) => `/api/admin/report-reasons/${id}`,
    update: (id) => `/api/admin/report-reasons/${id}`,
    delete: (id) => `/api/admin/report-reasons/${id}`,
  },
  reports: {
    list: '/api/admin/reports',
    update: (id) => `/api/admin/reports/${id}`,
    delete: (id) => `/api/admin/reports/${id}`,
    communities: '/api/admin/reports/communities-list', // Helper for dropdown
  },
  siteSettings: {
    get: '/api/admin/site-settings/get',
    update: '/api/admin/site-settings',
  },
  templateRequest: {
    create: '/api/admin/template-requests',
    list: '/api/admin/template-requests/list',
    updateStatus: (id) => `/api/admin/template-requests/${id}/status`,
    delete: (id) => `/api/admin/template-requests/${id}`,
    myStatus: '/api/admin/template-requests/my-status',
  },
  adminUsers: {
    list: '/api/admin/admin-users/list',
    create: '/api/admin/admin-users',
    update: (id) => `/api/admin/admin-users/${id}`,
    delete: (id) => `/api/admin/admin-users/${id}`,
    getById: (id) => `/api/admin/admin-users/${id}`,
    toggleStatus: (id) => `/api/admin/admin-users/${id}/toggle-status`,
  },
  users: {
    list: '/api/admin/users/list', // POST endpoint with field selection
    create: '/api/admin/users',
    update: (id) => `/api/admin/users/${id}`,
    updateExtraDetails: (id) => `/api/admin/users/${id}/extra-details`,
    delete: (id) => `/api/admin/users/${id}`,
    getById: (id) => `/api/admin/users/${id}`,
    toggleStatus: (id) => `/api/admin/users/${id}/toggle-status`,
    changePassword: (id) => `/api/admin/users/${id}/change-password`,
    templateDownload: '/api/admin/users/template/download',
    import: '/api/admin/users/import',
  },
  appUsers: {
    list: '/api/admin/app-users',
    create: '/api/admin/menus',
    details: (id) => `/api/admin/menus/${id}`,
    update: (id) => `/api/admin/menus/${id}`,
    delete: (id) => `/api/admin/menus/${id}`,
    toggleStatus: (id) => `/api/admin/menus/${id}/toggle-status`,
  },
  moduleMaster: {
    list: '/api/admin/modules/list',
    create: '/api/admin/modules',
    details: (id) => `/api/admin/modules/${id}`,
    update: (id) => `/api/admin/modules/${id}`,
    delete: (id) => `/api/admin/modules/${id}`,
    toggleStatus: (id) => `/api/admin/modules/${id}/toggle-status`,
    toggleShowInApp: (id) => `/api/admin/modules/${id}/toggle-show-in-app`,
  },
  menuMaster: {
    list: '/api/admin/menus/list',
    create: '/api/admin/menus',
    details: (id) => `/api/admin/menus/${id}`,
    update: (id) => `/api/admin/menus/${id}`,
    delete: (id) => `/api/admin/menus/${id}`,
    toggleStatus: (id) => `/api/admin/menus/${id}/toggle-status`,
    toggleShowInApp: (id) => `/api/admin/menus/${id}/toggle-show-in-app`,
  },
  featureMaster: {
    list: '/api/admin/features/list',
    create: '/api/admin/features',
    details: (id) => `/api/admin/features/${id}`,
    update: (id) => `/api/admin/features/${id}`,
    delete: (id) => `/api/admin/features/${id}`,
  },
  submenuMaster: {
    list: '/api/admin/submenus/list',
    create: '/api/admin/submenus',
    details: (id) => `/api/admin/submenus/${id}`,
    update: (id) => `/api/admin/submenus/${id}`,
    delete: (id) => `/api/admin/submenus/${id}`,
    toggleStatus: (id) => `/api/admin/submenus/${id}/toggle-status`,
    toggleShowInApp: (id) => `/api/admin/submenus/${id}/toggle-show-in-app`,
  },
  countryMaster: {
    list: '/api/admin/countries/list',
    create: '/api/admin/countries',
    details: (id) => `/api/admin/countries/${id}`,
    update: (id) => `/api/admin/countries/${id}`,
    delete: (id) => `/api/admin/countries/${id}`,
    toggleStatus: (id) => `/api/admin/countries/${id}/toggle-status`,
  },
  stateMaster: {
    list: '/api/admin/states/list',
    create: '/api/admin/states',
    details: (id) => `/api/admin/states/${id}`,
    update: (id) => `/api/admin/states/${id}`,
    delete: (id) => `/api/admin/states/${id}`,
    toggleStatus: (id) => `/api/admin/states/${id}/toggle-status`,
  },
  cityMaster: {
    list: '/api/admin/cities/list',
    create: '/api/admin/cities',
    details: (id) => `/api/admin/cities/${id}`,
    update: (id) => `/api/admin/cities/${id}`,
    delete: (id) => `/api/admin/cities/${id}`,
    toggleStatus: (id) => `/api/admin/cities/${id}/toggle-status`,
  },
  festivalMaster: {
    list: '/api/admin/festivals/list',
    create: '/api/admin/festivals',
    details: (id) => `/api/admin/festivals/${id}`,
    update: (id) => `/api/admin/festivals/${id}`,
    delete: (id) => `/api/admin/festivals/${id}`,
    toggleStatus: (id) => `/api/admin/festivals/${id}/toggle-status`,
    namesList: '/api/admin/festival-names',
    counts: '/api/admin/festivals/counts',
  },
  festival: {
    active: '/api/festival/active',
  },
  blockMaster: {
    list: '/api/admin/blocks/list',
    create: '/api/admin/blocks',
    details: (id) => `/api/admin/blocks/${id}`,
    update: (id) => `/api/admin/blocks/${id}`,
    delete: (id) => `/api/admin/blocks/${id}`,
    toggleStatus: (id) => `/api/admin/blocks/${id}/toggle-status`,
  },
  localityMaster: {
    list: '/api/admin/localities/list',
    create: '/api/admin/localities',
    details: (id) => `/api/admin/localities/${id}`,
    update: (id) => `/api/admin/localities/${id}`,
    delete: (id) => `/api/admin/localities/${id}`,
    toggleStatus: (id) => `/api/admin/localities/${id}/toggle-status`,
  },
  clusterMaster: {
    list: '/api/admin/clusters/list',
    create: '/api/admin/clusters',
    details: (id) => `/api/admin/clusters/${id}`,
    update: (id) => `/api/admin/clusters/${id}`,
    delete: (id) => `/api/admin/clusters/${id}`,
    toggleStatus: (id) => `/api/admin/clusters/${id}/toggle-status`,
  },
  designationMaster: {
    list: '/api/admin/designations/list',
    create: '/api/admin/designations',
    details: (id) => `/api/admin/designations/${id}`,
    update: (id) => `/api/admin/designations/${id}`,
    delete: (id) => `/api/admin/designations/${id}`,
    toggleStatus: (id) => `/api/admin/designations/${id}/toggle-status`,
    templateDownload: '/api/admin/designations/template/download',
    import: '/api/admin/designations/import',
  },
  departmentMaster: {
    list: '/api/admin/departments/list',
    create: '/api/admin/departments',
    details: (id) => `/api/admin/departments/${id}`,
    update: (id) => `/api/admin/departments/${id}`,
    delete: (id) => `/api/admin/departments/${id}`,
    toggleStatus: (id) => `/api/admin/departments/${id}/toggle-status`,
  },
  expenseCategory: {
    list: '/api/admin/expense-categories/list',
    create: '/api/admin/expense-categories',
    details: (id) => `/api/admin/expense-categories/${id}`,
    update: (id) => `/api/admin/expense-categories/${id}`,
    delete: (id) => `/api/admin/expense-categories/${id}`,
    toggleStatus: (id) => `/api/admin/expense-categories/${id}/toggle-status`,
    templateDownload: '/api/admin/expense-categories/template/download',
    import: '/api/admin/expense-categories/import',
  },
  expenseTransaction: {
    list: '/api/admin/expense-transactions/list', // Updated to POST
    create: '/api/admin/expense-transactions',
    details: (id) => `/api/admin/expense-transactions/${id}`,
    update: (id) => `/api/admin/expense-transactions/${id}`,
    delete: (id) => `/api/admin/expense-transactions/${id}`,
    approve: (id) => `/api/admin/expense-transactions/${id}/approve`,
    reject: (id) => `/api/admin/expense-transactions/${id}/reject`,
    pendingApprovals: '/api/admin/expense-transactions/pending-approvals/list', // Updated to POST
    bulkApprove: '/api/admin/expense-transactions/bulk-approve',
    bulkReject: '/api/admin/expense-transactions/bulk-reject',
    voucher: (id) => `/api/admin/expense-transactions/${id}/voucher`,
  },
  leaveTypeMaster: {
    list: '/api/admin/leave-types/list',
    create: '/api/admin/leave-types',
    details: (id) => `/api/admin/leave-types/${id}`,
    update: (id) => `/api/admin/leave-types/${id}`,
    delete: (id) => `/api/admin/leave-types/${id}`,
    toggleStatus: (id) => `/api/admin/leave-types/${id}/toggle-status`,
  },
  holidayCalendar: {
    list: '/api/admin/holidays/list',
    create: '/api/admin/holidays',
    details: (id) => `/api/admin/holidays/${id}`,
    update: (id) => `/api/admin/holidays/${id}`,
    delete: (id) => `/api/admin/holidays/${id}`,
    toggleStatus: (id) => `/api/admin/holidays/${id}/toggle-status`,
  },
  navigation: {
    menu: '/api/admin/navigation/menu',
  },
  // User Rights Management
  userGroup: {
    list: '/api/admin/user-groups/list', // Updated to POST
    create: '/api/admin/user-groups',
    details: (id) => `/api/admin/user-groups/${id}`,
    update: (id) => `/api/admin/user-groups/${id}`,
    delete: (id) => `/api/admin/user-groups/${id}`,
    templateDownload: '/api/admin/user-groups/template/download',
    import: '/api/admin/user-groups/import',
  },
  userRights: {
    get: '/api/admin/user-rights/get', // POST endpoint
    getAll: '/api/admin/user-rights/all',
    preview: '/api/admin/user-rights/preview',
    save: '/api/admin/user-rights/save',
  },
  specificUserRights: {
    get: (userId) => `/api/admin/specific-user-rights/${userId}`,
    update: (userId) => `/api/admin/specific-user-rights/${userId}`,
    navigation: (userId) => `/api/admin/specific-user-rights/${userId}/navigation`,
    recalculate: (userId) => `/api/admin/specific-user-rights/${userId}/recalculate`,
    override: '/api/admin/specific-user-rights/override',
  },
  planMaster: {
    list: '/api/admin/plans/list',
    create: '/api/admin/plans',
    details: (id) => `/api/admin/plans/${id}`,
    update: (id) => `/api/admin/plans/${id}`,
    delete: (id) => `/api/admin/plans/${id}`,
    toggleStatus: (id) => `/api/admin/plans/${id}/status`,
  },
  storageMaster: {
    list: '/api/admin/storage-master/list',
    create: '/api/admin/storage-master',
    details: (id) => `/api/admin/storage-master/${id}`,
    update: (id) => `/api/admin/storage-master/${id}`,
    delete: (id) => `/api/admin/storage-master/${id}`,
    toggleStatus: (id) => `/api/admin/storage-master/${id}/status`,
  },
  attendance: {
    status: '/api/admin/attendance/status',
    punchIn: '/api/admin/attendance/punch-in',
    punchOut: '/api/admin/attendance/punch-out',
    pause: '/api/admin/attendance/pause',
    resume: '/api/admin/attendance/resume',
    myHistory: '/api/admin/attendance/my-history',
    manage: '/api/admin/attendance/manage',
    updateStatus: (id) => `/api/admin/attendance/manage/${id}/status`, // Renamed from approveReject
    bulkUpdateStatus: '/api/admin/attendance/manage/bulk/status', // New bulk endpoint
    getById: '/api/admin/attendance/manage-details',
    drp: '/api/admin/attendance/drp-details',
    locationTrack: (attendanceId) => `/api/admin/attendance/location-track/${attendanceId}`,
  },
  pdfTemplates: {
    list: '/api/admin/pdf-templates/list',
    seed: '/api/admin/pdf-templates/seed',
  },
  leaveApplication: {
    list: '/api/admin/leave-applications/list', // Updated to POST
    create: '/api/admin/leave-applications',
    details: (id) => `/api/admin/leave-applications/${id}`,
    update: (id) => `/api/admin/leave-applications/${id}`,
    delete: (id) => `/api/admin/leave-applications/${id}`,
    approve: (id) => `/api/admin/leave-applications/${id}/approve`,
    reject: (id) => `/api/admin/leave-applications/${id}/reject`,
    bulkApprove: '/api/admin/leave-applications/bulk/approve',
    bulkReject: '/api/admin/leave-applications/bulk/reject',
    myApplications: '/api/admin/leave-applications/my/list', // Updated to POST
    pendingApprovals: '/api/admin/leave-applications/pending/list', // Updated to POST
    availableCompOffDates: '/api/admin/leave-applications/available-comp-off-dates',
    leaveBalance: '/api/admin/leave-applications/leave-balance',
    // Enhancement 7: Cancellation
    requestCancellation: (id) => `/api/admin/leave-applications/${id}/request-cancellation`,
    approveCancellation: (id) => `/api/admin/leave-applications/${id}/approve-cancellation`,
    rejectCancellation: (id) => `/api/admin/leave-applications/${id}/reject-cancellation`,
    cancellationList: '/api/admin/leave-applications/cancellation/list',
    applicableUsers: '/api/admin/leave-applications/applicable-users',
    applyOnBehalf: '/api/admin/leave-applications/apply-on-behalf',
  },
  // Enhancement 1 & 2: Leave Balance
  leaveBalance: {
    my: '/api/admin/leave-balance/my',
    allUsers: '/api/admin/leave-balance/all-users',
    user: (userId) => `/api/admin/leave-balance/user/${userId}`,
    initialize: '/api/admin/leave-balance/initialize',
    carryForward: '/api/admin/leave-balance/carry-forward',
    adjust: '/api/admin/leave-balance/adjust',
    adjustments: (userId) => `/api/admin/leave-balance/adjustments/${userId}`,
    deleteAdjustment: (id) => `/api/admin/leave-balance/adjust/${id}`,
  },
  // Enhancement 3: Leave Calendar
  leaveCalendar: {
    data: '/api/admin/leave-calendar',
    availability: '/api/admin/leave-calendar/availability',
  },
  unifiedCalendar: {
    events: '/api/admin/unified-calendar/events',
  },
  // Enhancement 4: Leave Analytics
  leaveAnalytics: {
    summary: '/api/admin/leave-analytics/summary',
    byType: '/api/admin/leave-analytics/by-type',
    topAbsentees: '/api/admin/leave-analytics/top-absentees',
    heatmap: '/api/admin/leave-analytics/heatmap',
  },
  // Enhancement 5: Leave Encashment
  leaveEncashment: {
    list: '/api/admin/leave-encashment/list',
    create: '/api/admin/leave-encashment',
    approve: (id) => `/api/admin/leave-encashment/${id}/approve`,
    reject: (id) => `/api/admin/leave-encashment/${id}/reject`,
    pendingForPayroll: '/api/admin/leave-encashment/pending-for-payroll',
  },
  project: {
    root: '/api/admin/projects',
    list: '/api/admin/projects/list', // Updated to POST
    create: '/api/admin/projects',
    details: (id) => `/api/admin/projects/${id}`,
    update: (id) => `/api/admin/projects/${id}`,
    delete: (id) => `/api/admin/projects/${id}`,
    export: (id) => `/api/admin/projects/${id}/export`,
  },
  projectAssignment: {
    getByProject: (projectId) => `/api/admin/project-assignments/project/${projectId}`,
    members: (projectId) => `/api/admin/project-assignments/project/${projectId}/members`,
    createOrUpdate: '/api/admin/project-assignments',
    delete: (projectId) => `/api/admin/project-assignments/project/${projectId}`,
    getUserProjects: (userId) => `/api/admin/project-assignments/user/${userId}`,
    checkCreatorRights: '/api/admin/project-assignments/check-creator-rights',
  },
  projectManagementMaster: {
    list: '/api/admin/project-management/list',
    create: '/api/admin/project-management',
    details: (id) => `/api/admin/project-management/${id}`,
    update: (id) => `/api/admin/project-management/${id}`,
    delete: (id) => `/api/admin/project-management/${id}`,
    toggleStatus: (id) => `/api/admin/project-management/${id}/toggle-status`,
  },
  forms: {
    list: '/api/admin/forms/list', // Updated to POST
    primaryDropdown: '/api/admin/forms/primary-dropdown',
    create: '/api/admin/forms',
    details: (id) => `/api/admin/forms/${id}`,
    update: (id) => `/api/admin/forms/${id}`,
    delete: (id) => `/api/admin/forms/${id}`,
    verifyAndFetch: '/api/admin/forms/verify-and-fetch',
    markAsExported: (id) => `/api/admin/forms/${id}/mark-exported`,
    // Analytics Studio
    pivotConfigs: (id) => `/api/admin/forms/${id}/pivot-configs`,
  },
  formData: {
    root: '/api/admin/form-data',
    exportPivot: '/api/admin/form-data/export-pivot',
  },
  formDrafts: {
    save: '/api/admin/form-drafts',
    get: '/api/admin/form-drafts', // with query params
    my: '/api/admin/form-drafts/list', // Changed to POST
    delete: (id) => `/api/admin/form-drafts/${id}`,
  },
  storage: {
    analytics: '/api/admin/storage/analytics', // POST
    breakdown: '/api/admin/storage/breakdown', // POST
    largeFiles: '/api/admin/storage/large-files', // POST
    deleteLargeFile: (key) => `/api/admin/storage/large-files/${key}`, // DELETE
    recalculate: '/api/admin/storage/recalculate', // POST
    purchase: '/api/admin/storage/purchase', // POST
  },
  drive: {
    list: '/api/drive/list', // Changed to POST /list
    root: '/api/drive',
    createFolder: '/api/drive/folder',
    upload: '/api/drive/upload',
    delete: (id) => `/api/drive/${id}`,
    share: '/api/drive/share',
    rename: '/api/drive/rename',
    cleanupStats: '/api/drive/cleanup/stats', // POST
    cleanup: '/api/drive/cleanup', // POST
    download: '/api/drive/download', // POST
    downloadFolder: (id) => `/api/drive/download/folder/${id}`, // GET
    move: '/api/drive/move', // POST
    emptyTrash: '/api/drive/empty-trash', // DELETE
  },
  themeSettings: {
    get: '/api/admin/theme-settings',
    update: '/api/admin/theme-settings',
    reset: '/api/admin/theme-settings/reset',
    bulkUpdate: '/api/admin/theme-settings/bulk-update',
  },
  vehicleTypeMaster: {
    list: '/api/admin/travel-management/vehicle-types/list',
    create: '/api/admin/travel-management/vehicle-types',
    details: (id) => `/api/admin/travel-management/vehicle-types/${id}`,
    update: (id) => `/api/admin/travel-management/vehicle-types/${id}`,
    delete: (id) => `/api/admin/travel-management/vehicle-types/${id}`,
    toggleStatus: (id) => `/api/admin/travel-management/vehicle-types/${id}/toggle-status`,
  },
  vehicleMileage: {
    list: '/api/admin/travel-management/vehicle-mileage/list',
    create: '/api/admin/travel-management/vehicle-mileage',
    details: (id) => `/api/admin/travel-management/vehicle-mileage/${id}`,
    update: (id) => `/api/admin/travel-management/vehicle-mileage/${id}`,
    delete: (id) => `/api/admin/travel-management/vehicle-mileage/${id}`,
    toggleStatus: (id) => `/api/admin/travel-management/vehicle-mileage/${id}/toggle-status`,
    calculateExpense: '/api/admin/travel-management/vehicle-mileage/calculate-expense',
  },
  dailyPlanner: {
    list: '/api/admin/daily-planner/list',
    create: '/api/admin/daily-planner',
    details: (id) => `/api/admin/daily-planner/${id}`,
    update: (id) => `/api/admin/daily-planner/${id}`,
    delete: (id) => `/api/admin/daily-planner/${id}`,
    toggleStatus: (id) => `/api/admin/daily-planner/${id}/toggle-status`,
    import: '/api/admin/daily-planner/import',
    templateDownload: '/api/admin/daily-planner/template/download',
  },
  geoFencing: {
    list: '/api/admin/geo-fencing/list',
    create: '/api/admin/geo-fencing',
    details: (id) => `/api/admin/geo-fencing/${id}`,
    update: (id) => `/api/admin/geo-fencing/${id}`,
    delete: (id) => `/api/admin/geo-fencing/${id}`,
    toggleStatus: (id) => `/api/admin/geo-fencing/${id}/toggle-status`,
    validateLocation: '/api/admin/geo-fencing/validate-location',
    assignUsers: (id) => `/api/admin/geo-fencing/${id}/assign-users`,
  },
  announcement: {
    list: '/api/admin/announcements/list',
    myNotifications: '/api/admin/announcements/my-notifications', // No role restriction — any authenticated user
    updateActionStatus: '/api/admin/announcements/my-notifications/action-status', // Sync leave action status
    create: '/api/admin/announcements',
    details: (id) => `/api/admin/announcements/${id}`,
    update: (id) => `/api/admin/announcements/${id}`,
    delete: (id) => `/api/admin/announcements/${id}`,
  },
  notificationSettings: {
    root: '/api/admin/notification-settings',
  },
  website: {
    list: '/api/admin/websites/list',
    create: '/api/admin/websites',
    getById: (id) => `/api/admin/websites/${id}`,
    update: (id) => `/api/admin/websites/${id}`,
    delete: (id) => `/api/admin/websites/${id}`,
    publish: (id) => `/api/admin/websites/${id}/publish`,
    unpublish: (id) => `/api/admin/websites/${id}/unpublish`,
    revert: (id, v) => `/api/admin/websites/${id}/revert/${v}`,
    duplicate: (id) => `/api/admin/websites/${id}/duplicate`,
    checkSlug: (slug) => `/api/admin/websites/check-slug/${slug}`,
    uploadImage: (id) => `/api/admin/websites/${id}/upload-image`,
    driveImages: (id) => `/api/admin/websites/${id}/drive-images`,
    aiGenerate: (id) => `/api/admin/websites/${id}/ai-generate`,
    checkAccessibility: (id) => `/api/admin/websites/${id}/check-accessibility`,
    verifyDomain: (id) => `/api/admin/websites/${id}/verify-domain`,
    analytics: (id) => `/api/admin/websites/${id}/analytics`,
    submissions: (id) => `/api/admin/websites/${id}/submissions/list`,
    exportSubmissions: (id) => `/api/admin/websites/${id}/submissions/export`,
  },
  // ─── Chat ────────────────────────────────────────────────────────────────────
  chat: {
    conversations: '/api/admin/chat/conversations',
    conversation: (id) => `/api/admin/chat/conversations/${id}`,
    messages: (id) => `/api/admin/chat/conversations/${id}/messages`,
    sendMessage: (id) => `/api/admin/chat/conversations/${id}/messages`,
    media: (id) => `/api/admin/chat/conversations/${id}/media`,
    editMessage: (msgId) => `/api/admin/chat/messages/${msgId}`,
    deleteMessage: (msgId) => `/api/admin/chat/messages/${msgId}`,
    react: (msgId) => `/api/admin/chat/messages/${msgId}/react`,
    markRead: (msgId) => `/api/admin/chat/messages/${msgId}/read`,
    pollVote: (msgId) => `/api/admin/chat/messages/${msgId}/poll-vote`,
    conversationMedia: (id) => `/api/admin/chat/conversations/${id}/media`,
    participants: (id) => `/api/admin/chat/conversations/${id}/participants`,
    removeParticipant: (id, userId) => `/api/admin/chat/conversations/${id}/participants/${userId}`,
    updateParticipantRole: (id, userId) =>
      `/api/admin/chat/conversations/${id}/participants/${userId}/role`,
    pinMessage: (id, msgId) => `/api/admin/chat/conversations/${id}/messages/${msgId}/pin`,
    unpinMessage: (id, msgId) => `/api/admin/chat/conversations/${id}/messages/${msgId}/unpin`,
    mute: (id) => `/api/admin/chat/conversations/${id}/mute`,
    archive: (id) => `/api/admin/chat/conversations/${id}/archive`,
    users: '/api/admin/chat/users',
    fcmToken: '/api/admin/chat/fcm-token',
    liveKitToken: (id) => `/api/admin/chat/conversations/${id}/livekit-token`,
  },
  system: {
    backup: '/api/admin/system/backup',
  },
  hrmStats: {
    my: '/api/admin/hrm-stats/my',
    team: '/api/admin/hrm-stats/team',
    org: '/api/admin/hrm-stats/org',
  },
  customDashboards: {
    list: '/api/admin/custom-dashboards',
    create: '/api/admin/custom-dashboards',
    getById: (id) => `/api/admin/custom-dashboards/${id}`,
    update: (id) => `/api/admin/custom-dashboards/${id}`,
    delete: (id) => `/api/admin/custom-dashboards/${id}`,
    updateSharing: (id) => `/api/admin/custom-dashboards/${id}/sharing`,
    duplicate: (id) => `/api/admin/custom-dashboards/${id}/duplicate`,
  },
  formAggregation: {
    aggregate: (formId) => `/api/admin/form-aggregation/${formId}/aggregate`,
    tableData: (formId) => `/api/admin/form-aggregation/${formId}/table`,
    mapData: (formId) => `/api/admin/form-aggregation/${formId}/map-data`,
    // Analytics Studio
    pivot: (formId) => `/api/admin/form-aggregation/${formId}/pivot`,
    distinctValues: (formId, field) =>
      `/api/admin/form-aggregation/${formId}/distinct-values?field=${encodeURIComponent(field)}`,
    crossFormKpi: '/api/admin/form-aggregation/cross-form-kpi',
    sharedFields: '/api/admin/form-aggregation/shared-fields',
  },
  // ─── FieldStudio ─────────────────────────────────────────────────────────────
  fieldStudio: {
    list: '/api/fieldstudio/list',
    create: '/api/fieldstudio',
    get: (id) => `/api/fieldstudio/${id}`,
    update: (id) => `/api/fieldstudio/${id}`,
    delete: (id) => `/api/fieldstudio/${id}`,
    export: (id) => `/api/fieldstudio/${id}/export`,
    saveToDrive: (id) => `/api/fieldstudio/${id}/save-to-drive`,
    thumbnail: (id) => `/api/fieldstudio/${id}/thumbnail`,
    publish: (id) => `/api/fieldstudio/${id}/publish`,
    duplicate: (id) => `/api/fieldstudio/${id}/duplicate`,
  },
  fieldStudioTemplates: {
    list: '/api/fieldstudio-templates',
    use: (id) => `/api/fieldstudio-templates/${id}/use`,
    create: '/api/fieldstudio-templates',
    update: (id) => `/api/fieldstudio-templates/${id}`,
    delete: (id) => `/api/fieldstudio-templates/${id}`,
  },
  fieldStudioData: {
    projects: '/api/fieldstudio-data/projects',
    forms: (pid) => `/api/fieldstudio-data/projects/${pid}/forms`,
    fields: (fid) => `/api/fieldstudio-data/forms/${fid}/fields`,
    aggregate: (fid) => `/api/fieldstudio-data/forms/${fid}/aggregate`,
  },

  // ─── AI Image Tools ──────────────────────────────────────────────────────
  ai: {
    removeBackground: '/api/ai/remove-background',
    magicErase: '/api/ai/magic-erase',
    generativeFill: '/api/ai/generative-fill',
    generateVariations: '/api/ai/variations',
  },

  // ─── Payroll ──────────────────────────────────────────────────────────────
  payroll: {
    generate: '/api/admin/payroll/generate',
    generateSingle: '/api/admin/payroll/generate-single',
    list: '/api/admin/payroll/list',
    summary: '/api/admin/payroll/summary',
    mySlips: '/api/admin/payroll/my-slips',
    publishBulk: '/api/admin/payroll/publish-bulk',
    getById: (id) => `/api/admin/payroll/${id}`,
    update: (id) => `/api/admin/payroll/${id}`,
    publish: (id) => `/api/admin/payroll/${id}/publish`,
    delete: (id) => `/api/admin/payroll/${id}`,
    downloadPdf: (id, showSignature = true) => `/api/admin/payroll/payslip-pdf?id=${id}&showSignature=${showSignature}`,
  },

  // ─── Employee Salary ──────────────────────────────────────────────────────
  employeeSalary: {
    create: '/api/admin/employee-salary/create',
    list: '/api/admin/employee-salary/list',
    listByMonth: '/api/admin/employee-salary/list-by-month',
    getById: (id) => `/api/admin/employee-salary/${id}`,
    update: (id) => `/api/admin/employee-salary/${id}`,
    delete: (id) => `/api/admin/employee-salary/${id}`,
    byUser: (uid) => `/api/admin/employee-salary/user/${uid}`,
    activeByUser: (uid) => `/api/admin/employee-salary/user/${uid}/active`,
  },

  // ─── Salary Advance / Loan ────────────────────────────────────────────────
  salaryAdvance: {
    list: '/api/admin/salary-advances/list',
    create: '/api/admin/salary-advances',
    getById: (id) => `/api/admin/salary-advances/${id}`,
    update: (id) => `/api/admin/salary-advances/${id}`,
    delete: (id) => `/api/admin/salary-advances/${id}`,
    approve: (id) => `/api/admin/salary-advances/${id}/approve`,
    reject: (id) => `/api/admin/salary-advances/${id}/reject`,
  },

  // ─── Resignation ─────────────────────────────────────────────────────────
  resignation: {
    myApplications: '/api/admin/resignations/my/list', // POST — user's own
    pendingApprovals: '/api/admin/resignations/pending/list', // POST — manager/admin
    create: '/api/admin/resignations',
    details: (id) => `/api/admin/resignations/${id}`,
    update: (id) => `/api/admin/resignations/${id}`,
    withdraw: (id) => `/api/admin/resignations/${id}/withdraw`,
    accept: (id) => `/api/admin/resignations/${id}/accept`,
    reject: (id) => `/api/admin/resignations/${id}/reject`,
    retain: (id) => `/api/admin/resignations/${id}/retain`,
    updateDate: (id) => `/api/admin/resignations/${id}/update-date`,
    bulkAccept: '/api/admin/resignations/bulk/accept',
    bulkReject: '/api/admin/resignations/bulk/reject',
  },

  // ─── Staff Migration ─────────────────────────────────────────────────────
  staffMigration: {
    list: '/api/admin/staff-migrations/list',
    create: '/api/admin/staff-migrations',
    details: (id) => `/api/admin/staff-migrations/${id}`,
    execute: (id) => `/api/admin/staff-migrations/${id}/execute`,
    cancel: (id) => `/api/admin/staff-migrations/${id}/cancel`,
    checkCycle: '/api/admin/staff-migrations/check-cycle',
    preResignationCheck: (userId) => `/api/admin/staff-migrations/pre-resignation-check/${userId}`,
  },

  // ─── Forget Punch ────────────────────────────────────────────────────────
  forgetPunch: {
    myRequests: '/api/admin/forget-punch/my-requests', // GET — employee's own requests
    create: '/api/admin/forget-punch', // POST — create new request
    approvals: (status) => `/api/admin/forget-punch/approvals?status=${status}`, // GET — admin list
    updateStatus: (id) => `/api/admin/forget-punch/${id}/status`, // PATCH — approve/reject
  },

  // ─── Activity Log (Admin Audit Trail) ────────────────────────────────────
  activityLog: {
    checkEnabled: '/api/admin/activity-logs/check-enabled',
    list: '/api/admin/activity-logs/list',
    logVisit: '/api/admin/activity-logs/log-visit',
    stats: '/api/admin/activity-logs/stats',
    export: '/api/admin/activity-logs/export',
    modules: '/api/admin/activity-logs/modules',
    activeUsers: '/api/admin/activity-logs/users',
  },

  // ─── Document Type Master ─────────────────────────────────────────────────
  documentTypeMaster: {
    list: '/api/admin/document-type-master/list',
    active: '/api/admin/document-type-master/active',
    create: '/api/admin/document-type-master',
    details: (id) => `/api/admin/document-type-master/${id}`,
    update: (id) => `/api/admin/document-type-master/${id}`,
    delete: (id) => `/api/admin/document-type-master/${id}`,
    toggleStatus: (id) => `/api/admin/document-type-master/${id}/toggle-status`,
  },

  // ─── Document Request ─────────────────────────────────────────────────────
  documentRequest: {
    myRequests: '/api/admin/document-requests/my/list',
    pendingApprovals: '/api/admin/document-requests/pending/list',
    create: '/api/admin/document-requests',
    details: (id) => `/api/admin/document-requests/${id}`,
    update: (id) => `/api/admin/document-requests/${id}`,
    cancel: (id) => `/api/admin/document-requests/${id}/cancel`,
    updateStatus: (id) => `/api/admin/document-requests/${id}/status`,
    fulfill: (id) => `/api/admin/document-requests/${id}/fulfill`,
    download: (id) => `/api/admin/document-requests/${id}/download`,
  },

  // ─── WhatsApp Platform ──────────────────────────────────────────────────
  whatsapp: {
    // Connection health — centralized Field HRM account
    configStatus: '/api/admin/whatsapp/config/status',
    // Templates
    templates: '/api/admin/whatsapp/templates',
    templateSync: '/api/admin/whatsapp/templates/sync',
    template: (id) => `/api/admin/whatsapp/templates/${id}`,
    // Campaigns
    campaigns: '/api/admin/whatsapp/campaigns',
    campaignList: '/api/admin/whatsapp/campaigns/list',
    campaign: (id) => `/api/admin/whatsapp/campaigns/${id}`,
    campaignSend: (id) => `/api/admin/whatsapp/campaigns/${id}/send`,
    campaignStats: (id) => `/api/admin/whatsapp/campaigns/${id}/stats`,
    // Inbox
    inboxList: '/api/admin/whatsapp/inbox/list',
    inboxMessages: (id) => `/api/admin/whatsapp/inbox/${id}/messages`,
    inboxSend: (id) => `/api/admin/whatsapp/inbox/${id}/send`,
    inboxAssign: (id) => `/api/admin/whatsapp/inbox/${id}/assign`,
    inboxResolve: (id) => `/api/admin/whatsapp/inbox/${id}/resolve`,
    inboxReopen: (id) => `/api/admin/whatsapp/inbox/${id}/reopen`,
    inboxSuggestReply: (id) => `/api/admin/whatsapp/inbox/${id}/suggest-reply`,
    // Sequences
    sequences: '/api/admin/whatsapp/sequences',
    sequence: (id) => `/api/admin/whatsapp/sequences/${id}`,
    sequenceToggle: (id) => `/api/admin/whatsapp/sequences/${id}/toggle`,
    // Bot FAQs
    botFaqs: '/api/admin/whatsapp/bot-faqs',
    botFaq: (id) => `/api/admin/whatsapp/bot-faqs/${id}`,
    // Analytics
    analyticsOverview: '/api/admin/whatsapp/analytics/overview',
    analyticsTimeline: '/api/admin/whatsapp/analytics/timeline',
    analyticsCampaigns: '/api/admin/whatsapp/analytics/campaigns',
    analyticsContacts: '/api/admin/whatsapp/analytics/contacts',
    analyticsFaqStats: '/api/admin/whatsapp/analytics/faq-stats',
    analyticsBotActivity: '/api/admin/whatsapp/analytics/bot-activity',
    analyticsTopEmployees: '/api/admin/whatsapp/analytics/top-employees',
    analyticsStatusDist: '/api/admin/whatsapp/analytics/status-distribution',
    analyticsHealthScore: '/api/admin/whatsapp/analytics/health-score',
    analyticsHrFunnel: '/api/admin/whatsapp/analytics/hr-funnel',
    analyticsExportCsv: (days = 30) => `/api/admin/whatsapp/analytics/export-csv?days=${days}`,
    // Super Admin cross-org overview
    superOverview: '/api/admin/whatsapp/super/overview',
    // Logs
    logList: '/api/admin/whatsapp/logs/list',
    log: (id) => `/api/admin/whatsapp/logs/${id}`,
    // Org Bot Flow Config
    orgBotFlowOrgs: '/api/admin/whatsapp/org-bot-flow/orgs',
    orgBotFlowMyConfig: '/api/admin/whatsapp/org-bot-flow/my-config',
    orgBotFlowConfig: (orgId) => `/api/admin/whatsapp/org-bot-flow/${orgId}`,
    // Contacts (Phase E)
    contactsList: '/api/admin/whatsapp/contacts/list',
    contactsStats: '/api/admin/whatsapp/contacts/stats',
    contactOptIn: (id) => `/api/admin/whatsapp/contacts/${id}/opt-in`,
    contactsBulkOptIn: '/api/admin/whatsapp/contacts/bulk-opt-in',
    contactSendTest: (id) => `/api/admin/whatsapp/contacts/${id}/send-test`,
    contactsPayslip: '/api/admin/whatsapp/contacts/send-payslip',
    contactsBulkPayslip: '/api/admin/whatsapp/contacts/bulk-payslip',
    // Knowledge Base (Phase G)
    knowledgeBase: '/api/admin/whatsapp/knowledge-base',
    knowledgeUpload: '/api/admin/whatsapp/knowledge-base/upload',
    knowledgeDoc: (id) => `/api/admin/whatsapp/knowledge-base/${id}`,
    knowledgeQuery: '/api/admin/whatsapp/knowledge-base/query',
    // Flows (Phase D)
    flows: '/api/admin/whatsapp/flows',
    flow: (id) => `/api/admin/whatsapp/flows/${id}`,
    flowToggle: (id) => `/api/admin/whatsapp/flows/${id}/toggle`,
    flowDuplicate: (id) => `/api/admin/whatsapp/flows/${id}/duplicate`,
  },

  // ─── Public Forms (Smart Form / Career Portal) ────────────────────────────
  publicForms: {
    details: (orgId, formId) => `/api/public/forms/${orgId}/${formId}`,
    submit: (orgId, formId) => `/api/public/forms/${orgId}/${formId}/submit`,
    options: (orgId, formId) => `/api/public/forms/${orgId}/${formId}/options`,
    parseResume: (orgId, formId) => `/api/public/forms/${orgId}/${formId}/parse-resume`,
  },

  // ─── Hiring Management ────────────────────────────────────────────────────
  hiring: {
    // Jobs
    jobs: '/api/admin/hiring/jobs',
    job: (id) => `/api/admin/hiring/jobs/${id}`,
    // Applications
    applications: '/api/admin/hiring/applications',
    exportApplications: '/api/admin/hiring/applications/export',
    application: (id) => `/api/admin/hiring/applications/${id}`,
    moveStage: (id) => `/api/admin/hiring/applications/${id}/stage`,
    addNote: (id) => `/api/admin/hiring/applications/${id}/notes`,
    convertToTalentPool: (id) => `/api/admin/hiring/applications/${id}/convert-to-talent-pool`,
    generateOffer: (id) => `/api/admin/hiring/applications/${id}/generate-offer`,
    interviewPrep: (id) => `/api/admin/hiring/applications/${id}/interview-prep`,
    // AI Resume Parsing & Scoring
    parseResume: '/api/admin/hiring/parse-resume',
    scoreCandidate: (id) => `/api/admin/hiring/applications/${id}/score`,
    // Pipeline
    pipeline: '/api/admin/hiring/pipeline',
    // Interviews
    interviews: '/api/admin/hiring/interviews',
    interview: (id) => `/api/admin/hiring/interviews/${id}`,
    // Scorecards
    scorecards: '/api/admin/hiring/scorecards',
    scorecardSummary: (appId) => `/api/admin/hiring/scorecards/application/${appId}`,
    evaluationSummary: (id) => `/api/admin/hiring/applications/${id}/evaluation-summary`,
    // Talent Pool
    talentPool: '/api/admin/hiring/talent-pool',
    talentPoolEntry: (id) => `/api/admin/hiring/talent-pool/${id}`,
    talentPoolSuggest: '/api/admin/hiring/talent-pool/suggest',
    // Employee Referrals
    referrals: '/api/admin/hiring/referrals',
    referral: (id) => `/api/admin/hiring/referrals/${id}`,
    referralLeaderboard: '/api/admin/hiring/referrals/leaderboard',
    // Analytics
    analyticsOverview: '/api/admin/hiring/analytics/overview',
    // Onboarding Bridge
    convertToEmployee: (id) => `/api/admin/hiring/applications/${id}/convert-to-employee`,
    markConverted: (id) => `/api/admin/hiring/applications/${id}/mark-converted`,
    // Stats
    stats: '/api/admin/hiring/stats',
    // Settings (Career Portal)
    settings: '/api/admin/hiring/settings',
  },

  // ─── Public Careers Portal (no auth required) ─────────────────────────────
  publicCareers: {
    info: (slug) => `/api/public/careers/${slug}/info`,
    jobs: (slug) => `/api/public/careers/${slug}/jobs`,
    job: (slug, jobId) => `/api/public/careers/${slug}/jobs/${jobId}`,
    apply: (slug, jobId) => `/api/public/careers/${slug}/apply/${jobId}`,
    parseResume: '/api/public/careers/parse-resume',
  },

  // ─── Assets Management ───────────────────────────────────────────────────
  assets: {
    types: {
      list: '/api/admin/assets/types/list',
      create: '/api/admin/assets/types',
      update: (id) => `/api/admin/assets/types/${id}`,
      delete: (id) => `/api/admin/assets/types/${id}`,
      toggleStatus: (id) => `/api/admin/assets/types/${id}/toggle-status`,
    },
    suppliers: {
      list: '/api/admin/assets/suppliers/list',
      create: '/api/admin/assets/suppliers',
      update: (id) => `/api/admin/assets/suppliers/${id}`,
      delete: (id) => `/api/admin/assets/suppliers/${id}`,
      toggleStatus: (id) => `/api/admin/assets/suppliers/${id}/toggle-status`,
    },
    stock: {
      dashboard: '/api/admin/assets/stock/dashboard',
      lowAlerts: '/api/admin/assets/stock/low-alerts',
      transactions: '/api/admin/assets/stock/transactions',
      stockInQuantity: '/api/admin/assets/stock/stock-in-quantity',
    },
    devices: {
      list: '/api/admin/assets/devices/list',
      create: '/api/admin/assets/devices',
      update: (id) => `/api/admin/assets/devices/${id}`,
      delete: (id) => `/api/admin/assets/devices/${id}`,
      toggleStatus: (id) => `/api/admin/assets/devices/${id}/toggle-status`,
    },
    registry: {
      list: '/api/admin/assets/registry/list',
      create: '/api/admin/assets/registry',
      update: (id) => `/api/admin/assets/registry/${id}`,
      delete: (id) => `/api/admin/assets/registry/${id}`,
      bulk: '/api/admin/assets/registry/bulk',
    },
    requests: {
      list: '/api/admin/assets/requests/list',
      myList: '/api/admin/assets/requests/my-list',
      create: '/api/admin/assets/requests/my-request',
      approve: (id) => `/api/admin/assets/requests/${id}/approve`,
      reject: (id) => `/api/admin/assets/requests/${id}/reject`,
    },
    allocations: {
      list: '/api/admin/assets/allocations/list',
      allocate: '/api/admin/assets/allocations/allocate',
      return: (id) => `/api/admin/assets/allocations/${id}/return`,
    },
    myActive: '/api/admin/assets/my-active',
  },
  kraMaster: {
    list: '/api/admin/kra-master/list',
    create: '/api/admin/kra-master',
    update: (id) => `/api/admin/kra-master/${id}`,
    delete: (id) => `/api/admin/kra-master/${id}`,
    toggleStatus: (id) => `/api/admin/kra-master/${id}/toggle-status`,
  },
  myTasks: {
    list: '/api/admin/my-tasks/list',
    create: '/api/admin/my-tasks',
    start: (id) => `/api/admin/my-tasks/${id}/start`,
    complete: (id) => `/api/admin/my-tasks/${id}/complete`,
    pause: (id) => `/api/admin/my-tasks/${id}/pause`,
    resume: (id) => `/api/admin/my-tasks/${id}/resume`,
  },
};
